import React, { useContext, useState, useEffect } from "react";
import './OrderConfirm.css';
import { ShopContext } from "../../Context/ShopContext";
import { useNavigate } from "react-router-dom";

const LIGHT_COLORS = ['#FFFFFF','#FFFDD0','#F5F0DC','#FDD835','#C6EF3B','#D3D3D3'];

const SL_DISTRICTS = [
    'Ampara', 'Anuradhapura', 'Badulla', 'Batticaloa', 'Colombo',
    'Galle', 'Gampaha', 'Hambantota', 'Jaffna', 'Kalutara',
    'Kandy', 'Kegalle', 'Kilinochchi', 'Kurunegala', 'Mannar',
    'Matale', 'Matara', 'Monaragala', 'Mullaitivu', 'Nuwara Eliya',
    'Polonnaruwa', 'Puttalam', 'Ratnapura', 'Trincomalee', 'Vavuniya'
]

const OrderConfirm = () => {

    const { all_products, cartItems, getTotalCartPrice, clearCart } = useContext(ShopContext);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        fullName: "",
        addressLine: "",
        city: "",
        postalCode: "",
        contactNumber: "",
        district: ""
    });
    const [placing, setPlacing] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [submitError, setSubmitError] = useState('');

    // Shipping settings fetched from backend
    const [flatRate,      setFlatRate]      = useState(0)
    const [districtRates, setDistrictRates] = useState({})

    // Fetch shipping settings on mount
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/site-settings`)
            .then(r => r.json())
            .then(d => {
                if (d.success) {
                    setFlatRate(d.settings.shippingRate || 0)
                    setDistrictRates(d.settings.districtRates || {})
                }
            })
            .catch(console.error)
    }, [])

    // Calculate shipping based on selected district
    const shipping = formData.district && districtRates[formData.district] !== undefined
        ? districtRates[formData.district]
        : flatRate
    const subtotal   = getTotalCartPrice();
    const orderTotal = subtotal > 0 ? subtotal + shipping : 0;

    // Build display + submission lines from new cart structure
    const orderLines = Object.keys(cartItems).map(lineKey => {
        const line    = cartItems[lineKey];
        const product = all_products.find(p => p.id === Number(line.productId));
        if (!product) return null;
        return { lineKey, line, product };
    }).filter(Boolean);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setValidationError('');
    };

    const handleConfirmOrder = async () => {
        setValidationError('');
        setSubmitError('');

        if(!formData.fullName || !formData.addressLine || !formData.city || !formData.postalCode || !formData.contactNumber) {
            setValidationError("Please fill in all fields.");
            return;
        }

        if (!formData.district) {
            setValidationError("Please select your district.");
            return;
        }

        if (orderLines.length === 0) {
            setValidationError("Your cart is empty.");
            return;
        }

        setPlacing(true);

        const items = orderLines.map(({ line, product }) => ({   //build items for each order
            productId:     product.storeProductId,
            name:          product.name,
            image:         product.images[0],
            quantity:      line.quantity,
            price:         product.price,
            selectedSize:  line.size        || "",
            selectedColor: line.color?.name || ""
        }));

        const orderData = {
            shippingAddress: {
                fullName: formData.fullName,
                addressLine: formData.addressLine,
                city: formData.city,
                postalCode: formData.postalCode
            },
            contactNumber: formData.contactNumber,
            district: formData.district,
            items,
            totalAmount: orderTotal
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/place-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('auth-token')
                },
                body: JSON.stringify(orderData)
            });

            const data = await response.json();

            if (data.success) {
                clearCart();
                navigate('/order-success', { state: { orderId: data.orderId } });  // Redirect to success page with orderId
            } else {
                setSubmitError("Failed to place order: " + (data.error || "Please try again."));
            }
        } catch (error) {
            console.error("Order error:", error);
            setSubmitError("Something went wrong. Please check your connection and try again.");
        } finally {
            setPlacing(false);
        }
    };


    return (
        <div className="order-confirm">
            <div className="order-confirm-container">
                
                {/* order summary */}
                <div className="orderconfirm-summary">
                    <h2 className="orderconfirm-section-title">Your Order</h2>
 
                    <div className="orderconfirm-items">
                        {orderLines.length === 0 && (
                            <p className="orderconfirm-empty">Your cart is empty.</p>
                        )}
                        {orderLines.map(({ lineKey, line, product }) => {
                            const isLight = line.color && LIGHT_COLORS.includes(line.color.hex);
                            return (
                                <div key={lineKey} className="orderconfirm-item">
                                    <img
                                        src={product.images[0]}
                                        alt={product.name}
                                        className="orderconfirm-item-img"
                                    />
                                    <div className="orderconfirm-item-info">
                                        <p className="orderconfirm-item-name">{product.name}</p>
                                        <div className="orderconfirm-item-selections">
                                            {line.color && (
                                                <span className="orderconfirm-selection-tag">
                                                    <span
                                                        className="orderconfirm-color-dot"
                                                        style={{
                                                            background: line.color.hex,
                                                            border: isLight ? '1px solid #aaa' : 'none'
                                                        }}
                                                    />
                                                    {line.color.name}
                                                </span>
                                            )}
                                            {line.size && (
                                                <span className="orderconfirm-selection-tag">Size {line.size}</span>
                                            )}
                                        </div>
                                        <p className="orderconfirm-item-qty">Qty: {line.quantity}</p>
                                    </div>
                                    <p className="orderconfirm-item-price">
                                        Rs. {(product.price * line.quantity).toLocaleString()}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
 
                    <div className="orderconfirm-totals">
                        <div className="orderconfirm-total-row">
                            <span>Subtotal</span>
                            <span>Rs. {subtotal.toLocaleString()}</span>
                        </div>
                        <div className="orderconfirm-total-row">
                            <span>Shipping</span>
                            <span>Rs. {shipping.toLocaleString()}</span>
                        </div>
                        <hr className="orderconfirm-divider" />
                        <div className="orderconfirm-total-row orderconfirm-total-final">
                            <span>Total</span>
                            <span>Rs. {orderTotal.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* shipping form */}
                <div className="orderconfirm-form-wrap">
                    <h2 className="orderconfirm-section-title">Shipping Details</h2>

                    {/* API/network error banner — prominent, manual dismiss */}
                    {submitError && (
                        <div className="orderconfirm-submit-error">
                            <span>⚠ {submitError}</span>
                            <button className="orderconfirm-error-close" onClick={() => setSubmitError('')}>✕</button>
                        </div>
                    )}
                    <div className="orderconfirm-form">
                        <div className="orderconfirm-field">
                            <label>Full Name</label>
                            <input
                                type="text"
                                name="fullName"
                                placeholder="e.g. Kamal Perera"
                                value={formData.fullName}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="orderconfirm-field">
                            <label>Address</label>
                            <input
                                type="text"
                                name="addressLine"
                                placeholder="Street address, apartment, etc."
                                value={formData.addressLine}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="orderconfirm-field-row">
                            <div className="orderconfirm-field">
                                <label>City</label>
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Colombo"
                                    value={formData.city}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="orderconfirm-field">
                                <label>Postal Code</label>
                                <input
                                    type="text"
                                    name="postalCode"
                                    placeholder="10100"
                                    value={formData.postalCode}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <div className="orderconfirm-field">
                            <label>District <span className="orderconfirm-required">*</span></label>
                            <select
                                name="district"
                                value={formData.district}
                                onChange={handleChange}
                                className="orderconfirm-select"
                            >
                                <option value="">— Select your district —</option>
                                {SL_DISTRICTS.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                            {formData.district && districtRates[formData.district] !== undefined && (
                                <p className="orderconfirm-district-hint">
                                    Shipping to {formData.district}: Rs. {districtRates[formData.district].toLocaleString()}
                                </p>
                            )}
                        </div>
                        <div className="orderconfirm-field">
                            <label>Contact Number</label>
                            <input
                                type="text"
                                name="contactNumber"
                                placeholder="07X XXX XXXX"
                                value={formData.contactNumber}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
 
                    <div className="orderconfirm-payment-note">
                        <span className="orderconfirm-note-icon">ℹ</span>
                        <p>After confirming, you'll receive an Order ID. Send your bank payment slip along with the Order ID to our WhatsApp to complete the purchase.</p>
                    </div>

                    {/* Validation error — inline, auto-clears on input */}
                    {validationError && (
                        <div className="orderconfirm-validation-error">
                            ⚠ {validationError}
                        </div>
                    )}
 
                    <button
                        className="orderconfirm-confirm-btn"
                        onClick={handleConfirmOrder}
                        disabled={placing || orderLines.length === 0}
                    >
                        {placing ? 'Placing Order...' : 'Confirm Order'}
                    </button>
                </div>

            </div>
        </div>
    )
}

export default OrderConfirm;